import { useState, useCallback, useEffect } from 'react';
import useTetrazolioDetalhes from './useTetrazolioDetalhes';
import useAnaliseInicial from './useAnaliseInicial';
import useClassificacaoSementes from './useClassificacaoSementes';
import useTetrazolioConfiguracoes from './useTetrazolioConfiguracoes';
import tetrazolioService, { ImagemTetrazolio } from '../../../../services/sementes/tetrazolio';

interface UseFormularioTetrazolioResult {
  detalhes: ReturnType<typeof useTetrazolioDetalhes>['detalhes'];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  analiseInicial: ReturnType<typeof useAnaliseInicial>;
  classificacao: ReturnType<typeof useClassificacaoSementes>;
  configuracoes: ReturnType<typeof useTetrazolioConfiguracoes>;
  imagens: {
    imagens: ImagemTetrazolio[];
    loading: boolean;
    adicionarImagem: (imagem: Omit<ImagemTetrazolio, 'id'>) => Promise<void>;
    removerImagem: (imagemId: string) => Promise<void>;
  };
}

export default function useFormularioTetrazolio(id: string): UseFormularioTetrazolioResult {
  const {
    detalhes, loading, error, refetch,
  } = useTetrazolioDetalhes(id);
  const analiseInicial = useAnaliseInicial();
  const configuracoes = useTetrazolioConfiguracoes();
  const [loadingImagens, setLoadingImagens] = useState(false);
  const [imagensLocais, setImagensLocais] = useState<ImagemTetrazolio[]>([]);

  useEffect(() => {
    setImagensLocais(detalhes?.imagens || []);
  }, [detalhes]);

  const dadosAnaliseInicialFinal = detalhes?.dadosAnaliseInicial
    || analiseInicial.dadosAnaliseInicial;

  const classificacao = useClassificacaoSementes(
    dadosAnaliseInicialFinal,
    configuracoes.graus,
    detalhes?.lancamentosPorRepeticao,
  );

  const adicionarImagem = useCallback(async (imagem: Omit<ImagemTetrazolio, 'id'>) => {
    if (!id) return;

    setLoadingImagens(true);
    try {
      const novaImagem = await tetrazolioService.adicionarImagem(id, imagem);
      setImagensLocais((prevImagens) => [...prevImagens, novaImagem]);
    } catch (err) {
      console.error('Erro ao adicionar imagem:', err);
      throw err;
    } finally {
      setLoadingImagens(false);
    }
  }, [id]);

  const removerImagem = useCallback(async (imagemId: string) => {
    if (!id) return;

    setImagensLocais((prevImagens) => prevImagens.filter((img) => img.id !== imagemId));

    setLoadingImagens(true);
    try {
      await tetrazolioService.removerImagem(imagemId);
    } catch (err) {
      console.error('Erro ao remover imagem:', err);
      if (detalhes?.imagens) {
        setImagensLocais(detalhes.imagens);
      }
      throw err;
    } finally {
      setLoadingImagens(false);
    }
  }, [id, detalhes?.imagens]);

  return {
    detalhes,
    loading,
    error,
    refetch,
    analiseInicial,
    classificacao,
    configuracoes,
    imagens: {
      imagens: imagensLocais,
      loading: loadingImagens,
      adicionarImagem,
      removerImagem,
    },
  };
}
